import {
  BorderStyle,
  Document,
  ExternalHyperlink,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
  type IBorderOptions,
  type IParagraphOptions,
  type ParagraphChild,
} from "docx";
import { analyze, type BaseDirection } from "bidi-shaper";
import { jsPDF } from "jspdf";
import { lexer, type Token, type Tokens } from "marked";
import {
  ARABIC_FONT,
  baseDirection,
  ensureArabicFont,
  hasArabic,
  isBidiControl,
  ISOLATE_END,
  ISOLATE_START,
  setArabicFont,
  toVisual,
  wrapBidi,
} from "@/lib/pdfBidi";

function slugify(raw: string): string {
  return (
    raw
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "artifact"
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type InlineStyle = {
  bold?: boolean;
  italics?: boolean;
  strike?: boolean;
  code?: boolean;
};

type PdfRun = {
  text: string;
  bold?: boolean;
  italics?: boolean;
  code?: boolean;
  /** Self-contained token: resolve its direction from its own content. */
  isolate?: boolean;
};

function textRun(text: string, style: InlineStyle = {}): TextRun {
  return new TextRun({
    text,
    bold: style.bold || undefined,
    italics: style.italics || undefined,
    strike: style.strike || undefined,
    font: style.code ? "Courier New" : undefined,
    size: style.code ? 18 : 22,
  });
}

/** Walk marked inline tokens → docx runs (bold/italic/code/links/strike). */
function inlineToRuns(
  tokens: Token[] | undefined,
  style: InlineStyle = {},
): ParagraphChild[] {
  if (!tokens?.length) return [];
  const out: ParagraphChild[] = [];

  for (const token of tokens) {
    switch (token.type) {
      case "text": {
        const t = token as Tokens.Text;
        if (t.tokens?.length) out.push(...inlineToRuns(t.tokens, style));
        else out.push(textRun(t.text, style));
        break;
      }
      case "strong":
        out.push(
          ...inlineToRuns((token as Tokens.Strong).tokens, {
            ...style,
            bold: true,
          }),
        );
        break;
      case "em":
        out.push(
          ...inlineToRuns((token as Tokens.Em).tokens, {
            ...style,
            italics: true,
          }),
        );
        break;
      case "codespan":
        out.push(textRun((token as Tokens.Codespan).text, { ...style, code: true }));
        break;
      case "del":
        out.push(
          ...inlineToRuns((token as Tokens.Del).tokens, {
            ...style,
            strike: true,
          }),
        );
        break;
      case "link": {
        const link = token as Tokens.Link;
        const children = inlineToRuns(
          link.tokens?.length
            ? link.tokens
            : [{ type: "text", raw: link.text, text: link.text, escaped: false }],
          style,
        );
        out.push(
          new ExternalHyperlink({
            link: link.href,
            children: children.length
              ? children
              : [textRun(link.text || link.href, style)],
          }),
        );
        break;
      }
      case "image": {
        const image = token as Tokens.Image;
        out.push(
          textRun(image.text || image.href || "[image]", {
            ...style,
            italics: true,
          }),
        );
        break;
      }
      case "br":
        out.push(new TextRun({ break: 1 }));
        break;
      case "escape":
        out.push(textRun((token as Tokens.Escape).text, style));
        break;
      case "html":
        out.push(
          textRun((token as Tokens.HTML).text.replace(/<[^>]+>/g, ""), style),
        );
        break;
      default:
        if (
          "tokens" in token &&
          Array.isArray((token as { tokens?: Token[] }).tokens)
        ) {
          out.push(
            ...inlineToRuns((token as { tokens: Token[] }).tokens, style),
          );
        } else if (
          "text" in token &&
          typeof (token as { text: unknown }).text === "string"
        ) {
          out.push(textRun((token as { text: string }).text, style));
        }
        break;
    }
  }

  return out;
}

/** Same walk as DOCX, but flat runs for PDF layout. */
function tokensToPdfRuns(
  tokens: Token[] | undefined,
  style: InlineStyle = {},
): PdfRun[] {
  if (!tokens?.length) return [];
  const out: PdfRun[] = [];

  for (const token of tokens) {
    switch (token.type) {
      case "text": {
        const t = token as Tokens.Text;
        if (t.tokens?.length) out.push(...tokensToPdfRuns(t.tokens, style));
        else if (t.text) out.push({ text: t.text, ...style });
        break;
      }
      case "strong":
        out.push(
          ...tokensToPdfRuns((token as Tokens.Strong).tokens, {
            ...style,
            bold: true,
          }),
        );
        break;
      case "em":
        out.push(
          ...tokensToPdfRuns((token as Tokens.Em).tokens, {
            ...style,
            italics: true,
          }),
        );
        break;
      case "codespan":
        out.push({
          text: (token as Tokens.Codespan).text,
          ...style,
          code: true,
          isolate: true,
        });
        break;
      case "del":
        out.push(...tokensToPdfRuns((token as Tokens.Del).tokens, style));
        break;
      case "link": {
        const link = token as Tokens.Link;
        if (link.tokens?.length) out.push(...tokensToPdfRuns(link.tokens, style));
        else out.push({ text: link.text || link.href, ...style });
        break;
      }
      case "image": {
        const image = token as Tokens.Image;
        out.push({
          text: image.text || image.href || "[image]",
          ...style,
          italics: true,
        });
        break;
      }
      case "br":
        out.push({ text: "\n", ...style });
        break;
      case "escape":
        out.push({ text: (token as Tokens.Escape).text, ...style });
        break;
      case "html": {
        const text = (token as Tokens.HTML).text.replace(/<[^>]+>/g, "");
        if (text) out.push({ text, ...style });
        break;
      }
      default:
        if (
          "tokens" in token &&
          Array.isArray((token as { tokens?: Token[] }).tokens)
        ) {
          out.push(
            ...tokensToPdfRuns((token as { tokens: Token[] }).tokens, style),
          );
        } else if (
          "text" in token &&
          typeof (token as { text: unknown }).text === "string"
        ) {
          out.push({ text: (token as { text: string }).text, ...style });
        }
        break;
    }
  }

  return out;
}

function normalizePdfPunctuation(text: string): string {
  return text
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/\u00A0/g, " ");
}

function pdfSafe(text: string): string {
  return normalizePdfPunctuation(text).replace(/[\u200B-\u200D\uFEFF]/g, "");
}

/** Keeps ZWNJ/ZWJ, which Arabic/Persian shaping needs. */
function pdfSafeArabic(text: string): string {
  return normalizePdfPunctuation(text).replace(/[\u200B\uFEFF]/g, "");
}

const thinBorder: IBorderOptions = {
  style: BorderStyle.SINGLE,
  size: 4,
  color: "D0D0D6",
};
const tableBorders = {
  top: thinBorder,
  bottom: thinBorder,
  left: thinBorder,
  right: thinBorder,
};

function inlinePlainText(tokens: Token[] | undefined): string {
  return tokensToPdfRuns(tokens)
    .map((r) => r.text)
    .join("");
}

function isRtlText(text: string): boolean {
  return hasArabic(text) && baseDirection(text) === "rtl";
}

function cellParagraphs(tokens: Token[] | undefined, bold = false): Paragraph[] {
  const runs = inlineToRuns(tokens, bold ? { bold: true } : {});
  return [
    new Paragraph({
      bidirectional: isRtlText(inlinePlainText(tokens)) || undefined,
      children: runs.length ? runs : [textRun("")],
      spacing: { after: 0 },
    }),
  ];
}

function blocksToDocxChildren(tokens: Token[]): (Paragraph | Table)[] {
  const children: (Paragraph | Table)[] = [];

  const pushParagraph = (
    inlineTokens: Token[] | undefined,
    opts: Omit<IParagraphOptions, "children"> = {},
  ) => {
    const runs = inlineToRuns(inlineTokens);
    children.push(
      new Paragraph({
        ...opts,
        bidirectional: isRtlText(inlinePlainText(inlineTokens)) || undefined,
        children: runs.length ? runs : [textRun("")],
      }),
    );
  };

  for (const token of tokens) {
    switch (token.type) {
      case "space":
        break;
      case "heading": {
        const h = token as Tokens.Heading;
        const level =
          h.depth === 1
            ? HeadingLevel.HEADING_1
            : h.depth === 2
              ? HeadingLevel.HEADING_2
              : HeadingLevel.HEADING_3;
        pushParagraph(h.tokens, {
          heading: level,
          spacing: { before: 240, after: 120 },
        });
        break;
      }
      case "paragraph":
        pushParagraph((token as Tokens.Paragraph).tokens, {
          spacing: { after: 160 },
        });
        break;
      case "blockquote": {
        const q = token as Tokens.Blockquote;
        for (const inner of q.tokens) {
          if (inner.type === "paragraph") {
            pushParagraph((inner as Tokens.Paragraph).tokens, {
              indent: { left: 360 },
              spacing: { after: 120 },
              border: {
                left: {
                  style: BorderStyle.SINGLE,
                  size: 12,
                  color: "B0B0B8",
                  space: 12,
                },
              },
            });
          } else {
            children.push(...blocksToDocxChildren([inner]));
          }
        }
        break;
      }
      case "list": {
        const list = token as Tokens.List;
        const start =
          typeof list.start === "number" && list.start > 0 ? list.start : 1;
        list.items.forEach((item, index) => {
          const prefix = list.ordered ? `${start + index}. ` : "• ";
          const itemRuns = inlineToRuns(item.tokens);
          children.push(
            new Paragraph({
              bidirectional:
                isRtlText(inlinePlainText(item.tokens)) || undefined,
              children: [
                textRun(prefix),
                ...(itemRuns.length ? itemRuns : [textRun("")]),
              ],
              spacing: { after: 80 },
              indent: { left: 180 },
            }),
          );
        });
        break;
      }
      case "code": {
        const code = token as Tokens.Code;
        for (const line of code.text.split("\n")) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: line.length ? line : " ",
                  font: "Courier New",
                  size: 18,
                }),
              ],
              spacing: { after: 0 },
            }),
          );
        }
        children.push(new Paragraph({ text: "", spacing: { after: 140 } }));
        break;
      }
      case "hr":
        children.push(
          new Paragraph({
            border: {
              bottom: {
                style: BorderStyle.SINGLE,
                size: 6,
                color: "D0D0D6",
                space: 1,
              },
            },
            spacing: { before: 120, after: 120 },
          }),
        );
        break;
      case "table": {
        const table = token as Tokens.Table;
        const colCount = Math.max(table.header.length, 1);
        const colWidth = Math.floor(9000 / colCount);
        const rows: TableRow[] = [];

        rows.push(
          new TableRow({
            children: table.header.map(
              (cell) =>
                new TableCell({
                  borders: tableBorders,
                  width: { size: colWidth, type: WidthType.DXA },
                  children: cellParagraphs(cell.tokens, true),
                }),
            ),
          }),
        );

        for (const row of table.rows) {
          rows.push(
            new TableRow({
              children: row.map(
                (cell) =>
                  new TableCell({
                    borders: tableBorders,
                    width: { size: colWidth, type: WidthType.DXA },
                    children: cellParagraphs(cell.tokens),
                  }),
              ),
            }),
          );
        }

        children.push(
          new Table({
            width: { size: 9000, type: WidthType.DXA },
            columnWidths: Array.from({ length: colCount }, () => colWidth),
            rows,
          }),
        );
        children.push(new Paragraph({ text: "", spacing: { after: 160 } }));
        break;
      }
      case "html": {
        const html = (token as Tokens.HTML).text.replace(/<[^>]+>/g, "").trim();
        if (html) {
          children.push(
            new Paragraph({
              children: [textRun(html)],
              spacing: { after: 120 },
            }),
          );
        }
        break;
      }
      default:
        if (
          "tokens" in token &&
          Array.isArray((token as { tokens?: Token[] }).tokens)
        ) {
          children.push(
            ...blocksToDocxChildren((token as { tokens: Token[] }).tokens),
          );
        } else if (
          "text" in token &&
          typeof (token as { text: unknown }).text === "string"
        ) {
          children.push(
            new Paragraph({
              children: [textRun((token as { text: string }).text)],
              spacing: { after: 120 },
            }),
          );
        }
        break;
    }
  }

  return children;
}

// ── PDF (vector layout — no html2canvas) ─────────────────────────────────────



function applyPdfFont(
  doc: jsPDF,
  run: Pick<PdfRun, "bold" | "italics" | "code">,
  size: number,
) {
  doc.setFontSize(size);
  if (run.code) {
    doc.setFont("courier", "normal");
    return;
  }
  // Amiri covers Arabic + Latin. No italic face — bold when requested.
  doc.setFont(ARABIC_FONT, run.bold ? "bold" : "normal");
}

function measurePdfRun(doc: jsPDF, run: PdfRun, size: number): number {
  const text = pdfSafe(run.text);
  if (!text) return 0;
  applyPdfFont(doc, run, size);
  return doc.getTextWidth(text);
}

function drawPdfRun(doc: jsPDF, run: PdfRun, x: number, y: number, size: number) {
  const text = pdfSafe(run.text);
  if (!text) return 0;
  applyPdfFont(doc, run, size);
  doc.setTextColor(28, 28, 30);
  doc.text(text, x, y);
  return doc.getTextWidth(text);
}

// Arabic lines: wrap the LOGICAL text, shape per line at draw time. Styles
// survive via analyze()'s visual-to-logical map, so inline bold/code inside an
// Arabic paragraph keeps its face after reordering.

type StyledChar = { ch: string; style: PdfRun };
type VisualSeg = { text: string; style: PdfRun };
type StyledLine = { segments: VisualSeg[]; width: number };

function sameStyle(a: PdfRun, b: PdfRun): boolean {
  return (
    !!a.bold === !!b.bold && !!a.italics === !!b.italics && !!a.code === !!b.code
  );
}

function applySegFont(doc: jsPDF, style: PdfRun, text: string, size: number) {
  doc.setFontSize(size);
  if (style.code && !hasArabic(text)) {
    doc.setFont("courier", "normal");
    return;
  }
  doc.setFont(ARABIC_FONT, style.bold ? "bold" : "normal");
}

function layoutStyledLine(
  doc: jsPDF,
  chars: StyledChar[],
  size: number,
  direction: BaseDirection,
): StyledLine {
  if (!chars.length) return { segments: [], width: 0 };
  const a = analyze(chars.map((c) => c.ch).join(""), { direction });
  const segments: VisualSeg[] = [];
  let i = 0;
  for (const ch of a.text) {
    // Isolate markers steered the layout above; they have no glyph to draw.
    if (isBidiControl(ch)) {
      i += 1;
      continue;
    }
    const src = a.visualToLogical[i] ?? 0;
    const style = (chars[src] ?? chars[chars.length - 1]!).style;
    const last = segments[segments.length - 1];
    if (last && sameStyle(last.style, style)) last.text += ch;
    else segments.push({ text: ch, style });
    i += 1;
  }
  let width = 0;
  for (const seg of segments) {
    applySegFont(doc, seg.style, seg.text, size);
    width += doc.getTextWidth(seg.text);
  }
  return { segments, width };
}

function wrapStyledBidi(
  doc: jsPDF,
  runs: PdfRun[],
  maxWidth: number,
  size: number,
  direction: BaseDirection,
): StyledLine[] {
  const chars: StyledChar[] = [];
  for (const run of runs) {
    const isolated = run.isolate && hasArabic(run.text);
    if (isolated) chars.push({ ch: ISOLATE_START, style: run });
    for (const ch of pdfSafeArabic(run.text)) chars.push({ ch, style: run });
    if (isolated) chars.push({ ch: ISOLATE_END, style: run });
  }

  const lines: StyledChar[][] = [];
  let line: StyledChar[] = [];
  let lineW = 0;
  let pending: StyledChar[] = [];
  let pendingW = 0;

  const widthOfChars = (cs: StyledChar[]) =>
    layoutStyledLine(doc, cs, size, direction).width;

  let i = 0;
  while (i < chars.length) {
    const ch = chars[i]!.ch;
    if (ch === "\n") {
      lines.push(line);
      line = [];
      lineW = 0;
      pending = [];
      pendingW = 0;
      i += 1;
      continue;
    }
    if (/\s/.test(ch)) {
      if (line.length) {
        pending.push(chars[i]!);
        applySegFont(doc, chars[i]!.style, " ", size);
        pendingW += doc.getTextWidth(" ");
      }
      i += 1;
      continue;
    }
    let j = i;
    while (j < chars.length && !/\s/.test(chars[j]!.ch)) j += 1;
    const word = chars.slice(i, j);
    const wordW = widthOfChars(word);
    if (!line.length) {
      if (wordW > maxWidth) {
        let part: StyledChar[] = [];
        for (const c of word) {
          const next = [...part, c];
          if (part.length && widthOfChars(next) > maxWidth) {
            lines.push(part);
            part = [c];
          } else {
            part = next;
          }
        }
        line = part;
        lineW = widthOfChars(part);
      } else {
        line = word;
        lineW = wordW;
      }
    } else if (lineW + pendingW + wordW > maxWidth) {
      lines.push(line);
      line = [];
      lineW = 0;
      pending = [];
      pendingW = 0;
      continue;
    } else {
      line.push(...pending, ...word);
      lineW += pendingW + wordW;
      pending = [];
      pendingW = 0;
    }
    i = j;
  }
  if (line.length) lines.push(line);

  return lines.map((l) => layoutStyledLine(doc, l, size, direction));
}

function drawStyledLine(
  doc: jsPDF,
  line: StyledLine,
  leftX: number,
  rightX: number,
  y: number,
  size: number,
  rtl: boolean,
) {
  doc.setTextColor(28, 28, 30);
  let cx = rtl ? rightX - line.width : leftX;
  for (const seg of line.segments) {
    applySegFont(doc, seg.style, seg.text, size);
    doc.text(seg.text, cx, y);
    cx += doc.getTextWidth(seg.text);
  }
}

function wrapPdfRuns(
  doc: jsPDF,
  runs: PdfRun[],
  maxWidth: number,
  size: number,
): PdfRun[][] {
  const lines: PdfRun[][] = [[]];
  let lineWidth = 0;

  const pushLine = () => {
    lines.push([]);
    lineWidth = 0;
  };

  const appendToLine = (run: PdfRun) => {
    lines[lines.length - 1]!.push(run);
    lineWidth += measurePdfRun(doc, run, size);
  };

  for (const run of runs) {
    // Wrap on logical text; shape only when measuring/drawing so joins stay intact.
    const parts = pdfSafe(run.text).split(/(\s+)/);
    for (const part of parts) {
      if (!part) continue;
      if (part === "\n") {
        pushLine();
        continue;
      }
      const piece: PdfRun = { ...run, text: part };
      const w = measurePdfRun(doc, piece, size);
      if (lineWidth > 0 && lineWidth + w > maxWidth) {
        pushLine();
        if (/^\s+$/.test(part)) continue;
      }
      if (lines[lines.length - 1]!.length === 0 && /^\s+$/.test(part)) continue;
      appendToLine(piece);
    }
  }

  return lines.filter((line) => line.length > 0);
}

function drawPdfRunLine(
  doc: jsPDF,
  runs: PdfRun[],
  x: number,
  y: number,
  size: number,
) {
  let cx = x;
  for (const run of runs) {
    cx += drawPdfRun(doc, run, cx, y, size);
  }
}

function versionedFilename(title: string, version?: number): string {
  const suffix = version && version > 0 ? `-v${version}` : "";
  return `${slugify(title)}${suffix}`;
}

export function exportArtifactMarkdown(
  title: string,
  content: string,
  version?: number,
) {
  const body = content.endsWith("\n") ? content : `${content}\n`;
  downloadBlob(
    new Blob([body], { type: "text/markdown;charset=utf-8" }),
    `${versionedFilename(title, version)}.md`,
  );
}

export async function exportArtifactDocx(
  title: string,
  content: string,
  version?: number,
) {
  const tokens = lexer(content);
  const body = blocksToDocxChildren(tokens);

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 720,
            },
          },
        },
        children: [
          new Paragraph({
            children: [new TextRun({ text: title, bold: true, size: 32 })],
            spacing: { after: 200 },
          }),
          ...body,
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, `${versionedFilename(title, version)}.docx`);
}

/**
 * Vector PDF from the same marked GFM tokens as DOCX.
 * Uses Amiri + bidi-shaper so Arabic joins and reads correctly (Helvetica can't).
 */
export async function exportArtifactPdf(
  title: string,
  content: string,
  version?: number,
) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  await ensureArabicFont(doc);

  const pageW = 210;
  const pageH = 297;
  const margin = 16;
  const contentW = pageW - margin * 2;
  let y = margin;

  const lineH = (sizePt: number) => sizePt * 0.38;

  const ensure = (need: number) => {
    if (y + need <= pageH - margin) return;
    doc.addPage();
    y = margin;
  };

  const writeRuns = (runs: PdfRun[], size: number, after: number, indent = 0) => {
    const width = contentW - indent;
    const plain = runs.map((r) => r.text).join("");
    const lh = lineH(size);

    if (hasArabic(plain)) {
      const direction = baseDirection(plain);
      const rtl = direction === "rtl";
      const lines = wrapStyledBidi(doc, runs, width, size, direction);
      for (const line of lines) {
        ensure(lh + 0.8);
        drawStyledLine(
          doc,
          line,
          margin + indent,
          margin + width,
          y + lh * 0.8,
          size,
          rtl,
        );
        y += lh;
      }
      y += after;
      return;
    }

    const lines = wrapPdfRuns(
      doc,
      runs.length ? runs : [{ text: "" }],
      width,
      size,
    );
    for (const line of lines) {
      ensure(lh + 0.8);
      drawPdfRunLine(doc, line, margin + indent, y + lh * 0.8, size);
      y += lh;
    }
    y += after;
  };

  const writePlain = (
    text: string,
    size: number,
    after: number,
    opts?: { bold?: boolean; indent?: number; mono?: boolean },
  ) => {
    writeRuns(
      [
        {
          text,
          bold: opts?.bold,
          code: opts?.mono,
        },
      ],
      size,
      after,
      opts?.indent ?? 0,
    );
  };

  const writeTable = (table: Tokens.Table) => {
    const cellText = (tokens?: Token[]) =>
      tokensToPdfRuns(tokens)
        .map((r) =>
          r.isolate && hasArabic(r.text)
            ? `${ISOLATE_START}${r.text}${ISOLATE_END}`
            : r.text,
        )
        .join("");
    const header = table.header.map((c) => cellText(c.tokens));
    const body = table.rows.map((row) => row.map((c) => cellText(c.tokens)));
    const rows = [header, ...body];
    const colCount = Math.max(header.length, 1);
    const fontSize = 9;
    const padX = 1.6;
    const padY = 1.4;
    const lh = lineH(fontSize);
    const colW = contentW / colCount;

    const wrapped = rows.map((row, rowIndex) =>
      Array.from({ length: colCount }, (_, c) => {
        const raw = row[c] ?? "";
        applyPdfFont(doc, { bold: rowIndex === 0 }, fontSize);
        if (hasArabic(raw)) {
          const direction = baseDirection(raw);
          const lines = wrapBidi(doc, pdfSafeArabic(raw), colW - padX * 2).map(
            (l) => toVisual(l, direction) || " ",
          );
          return {
            lines: lines.length ? lines : [" "],
            rtl: direction === "rtl",
          };
        }
        const split = doc.splitTextToSize(pdfSafe(raw) || " ", colW - padX * 2);
        return {
          lines: (Array.isArray(split) ? split : [String(split)]).map(String),
          rtl: false,
        };
      }),
    );

    for (let r = 0; r < wrapped.length; r += 1) {
      const rowCells = wrapped[r]!;
      const rowLineCount = Math.max(...rowCells.map((c) => c.lines.length), 1);
      const rowH = rowLineCount * lh + padY * 2;
      ensure(rowH + 0.4);
      const top = y;

      for (let c = 0; c < colCount; c += 1) {
        const x = margin + c * colW;
        if (r === 0) {
          doc.setFillColor(245, 245, 247);
          doc.rect(x, top, colW, rowH, "F");
        }
        doc.setDrawColor(208, 208, 214);
        doc.setLineWidth(0.15);
        doc.rect(x, top, colW, rowH, "S");

        applyPdfFont(doc, { bold: r === 0 }, fontSize);
        doc.setTextColor(28, 28, 30);
        const cell = rowCells[c] ?? { lines: [""], rtl: false };
        let ty = top + padY + lh * 0.75;
        for (const line of cell.lines) {
          if (cell.rtl) doc.text(line, x + colW - padX, ty, { align: "right" });
          else doc.text(line, x + padX, ty);
          ty += lh;
        }
      }
      y += rowH;
    }
    y += 3.5;
  };

  // Title
  writePlain(title, 16, 3, { bold: true });
  doc.setDrawColor(220, 220, 226);
  doc.setLineWidth(0.2);
  ensure(2);
  doc.line(margin, y, pageW - margin, y);
  y += 5;

  for (const token of lexer(content)) {
    switch (token.type) {
      case "space":
        break;
      case "heading": {
        const h = token as Tokens.Heading;
        const size = h.depth === 1 ? 14 : h.depth === 2 ? 12 : 11;
        y += 1.5;
        writeRuns(tokensToPdfRuns(h.tokens, { bold: true }), size, 2.5);
        break;
      }
      case "paragraph":
        writeRuns(
          tokensToPdfRuns((token as Tokens.Paragraph).tokens),
          10,
          3,
        );
        break;
      case "blockquote": {
        const q = token as Tokens.Blockquote;
        for (const inner of q.tokens) {
          if (inner.type === "paragraph") {
            const innerRuns = tokensToPdfRuns((inner as Tokens.Paragraph).tokens);
            const rtl = isRtlText(innerRuns.map((r) => r.text).join(""));
            const startY = y;
            writeRuns(innerRuns, 10, 2.5, 4);
            const spineX = rtl ? pageW - margin - 1 : margin + 1;
            doc.setDrawColor(180, 180, 188);
            doc.setLineWidth(0.6);
            doc.line(spineX, startY, spineX, y - 2);
          }
        }
        break;
      }
      case "list": {
        const list = token as Tokens.List;
        const start =
          typeof list.start === "number" && list.start > 0 ? list.start : 1;
        list.items.forEach((item, index) => {
          const prefix = list.ordered ? `${start + index}. ` : "•  ";
          const runs: PdfRun[] = [
            { text: prefix },
            ...tokensToPdfRuns(item.tokens),
          ];
          writeRuns(runs, 10, 1.8, 2);
        });
        y += 1;
        break;
      }
      case "code": {
        const code = token as Tokens.Code;
        const size = 9;
        const lh = lineH(size);
        const codeLineBg = () => {
          ensure(lh + 0.6);
          doc.setFillColor(245, 245, 247);
          doc.rect(margin, y, contentW, lh + 0.4, "F");
        };
        for (const line of code.text.split("\n")) {
          if (hasArabic(line)) {
            const direction = baseDirection(line);
            setArabicFont(doc, false, size);
            const wrapped = wrapBidi(doc, pdfSafeArabic(line), contentW - 2.4);
            for (const w of wrapped.length ? wrapped : [""]) {
              codeLineBg();
              setArabicFont(doc, false, size);
              doc.setTextColor(28, 28, 30);
              doc.text(toVisual(w, direction) || " ", margin + 1.2, y + lh * 0.8);
              y += lh + 0.4;
            }
            continue;
          }
          applyPdfFont(doc, { code: true }, size);
          const wrapped = doc.splitTextToSize(pdfSafe(line) || " ", contentW - 2.4);
          const lines = Array.isArray(wrapped) ? wrapped : [String(wrapped)];
          for (const w of lines) {
            codeLineBg();
            drawPdfRun(
              doc,
              { text: String(w), code: true },
              margin + 1.2,
              y + lh * 0.8,
              size,
            );
            y += lh + 0.4;
          }
        }
        y += 2.5;
        break;
      }
      case "hr":
        ensure(4);
        y += 1.5;
        doc.setDrawColor(210, 210, 216);
        doc.setLineWidth(0.2);
        doc.line(margin, y, pageW - margin, y);
        y += 3.5;
        break;
      case "table":
        writeTable(token as Tokens.Table);
        break;
      case "html": {
        const text = (token as Tokens.HTML).text.replace(/<[^>]+>/g, "").trim();
        if (text) writePlain(text, 10, 2.5);
        break;
      }
      default:
        if (
          "tokens" in token &&
          Array.isArray((token as { tokens?: Token[] }).tokens)
        ) {
          writeRuns(
            tokensToPdfRuns((token as { tokens: Token[] }).tokens),
            10,
            2.5,
          );
        }
        break;
    }
  }

  doc.save(`${versionedFilename(title, version)}.pdf`);
}
